import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { reportsAPI } from '../../api';
import { useAppStore } from '../../stores/useAppStore';
import { Button } from '../ui';
import ReportBuilder from './ReportBuilder';
import SqlWorkbench from './SqlWorkbench';
import { extractApiError, summarise } from './lib/apiErrors';

/** /reports/:id/edit — loads the report and opens the matching editor for its kind. */
const ReportEditDispatcher = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addNotification } = useAppStore();
  const [report, setReport] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await reportsAPI.get(id);
        if (!cancelled) setReport(res.data);
      } catch (err) {
        if (cancelled) return;
        const apiErr = await extractApiError(err);
        if (apiErr.status === 404) setNotFound(true);
        else if (apiErr.status === 403) {
          addNotification({ type: 'error', message: 'Only admins can edit reports' });
          navigate('/reports');
        } else setError(summarise(apiErr));
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  if (notFound) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-16 text-center">
        <h1 className="text-2xl font-bold text-gray-900 mb-2">Report not found</h1>
        <Button onClick={() => navigate('/reports')}>Back to reports</Button>
      </div>
    );
  }
  if (error) {
    return <div className="max-w-3xl mx-auto px-4 py-16 text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg">{error}</div>;
  }
  if (!report) {
    return (
      <div className="min-h-[50vh] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600" />
      </div>
    );
  }
  return report.Kind === 'sql' ? <SqlWorkbench key={report.ID} report={report} /> : <ReportBuilder key={report.ID} report={report} />;
};

export default ReportEditDispatcher;
